'use client';

import { useEffect } from 'react';
import LocaleLink from '@/components/common/LocaleLink';
import { AlertTriangle, Home, RefreshCw, Mail } from 'lucide-react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error);
  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-red-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full text-center">
        
        {/* Error Icon */}
        <div className="relative mb-8">
          <div className="text-[120px] sm:text-[160px] lg:text-[200px] font-bold font-sans text-red-200 leading-none select-none">
            500
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-500 flex items-center justify-center shadow-lg animate-pulse">
              <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-light-text mb-4">
              Something Went Wrong
            </h2>
            <p className="text-lg text-light-text-secondary leading-relaxed">
              We&apos;re experiencing technical difficulties. Our team has been notified and is working on a fix.
            </p>
            
            {/* Error Details (only in development) */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4 p-4 bg-red-100 border border-red-200 rounded-lg text-left">
                <p className="text-sm font-mono text-red-800 break-all">
                  {error.message}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={reset}
              className="inline-flex items-center justify-center px-6 py-3 bg-jasper text-white font-medium rounded-lg hover:bg-jasper-700 transition-colors shadow-lg hover:shadow-xl"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Try Again
            </button>
            
            <LocaleLink
              href="/"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-jasper text-jasper font-medium rounded-lg hover:bg-jasper hover:text-white transition-colors"
            >
              <Home className="h-5 w-5 mr-2" />
              Go Home
            </LocaleLink>
          </div>

          {/* Contact Support */}
          <div className="pt-6">
            <p className="text-sm text-light-text-secondary mb-3">
              If the problem persists, please contact our support team:
            </p>
            <LocaleLink
              href="/contact"
              className="inline-flex items-center text-jasper hover:text-jasper-700 font-medium transition-colors"
            >
              <Mail className="h-4 w-4 mr-2" />
              Contact Support
            </LocaleLink>
          </div>
        </div>

        {/* Error Reference */}
        {error.digest && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-xs text-light-text-secondary">
              Error Reference: <code className="font-mono bg-gray-100 px-2 py-1 rounded">{error.digest}</code>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}